import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { APIWeb } from '@/api'
import { Loading } from 'react-vant'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import { useCurrencySelection, type CurrencyItem } from '@/contexts/AppStateContext'
import Ripple from '@/components/Ripple'
export default React.memo(function SelectCurrency() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { actions } = useCurrencySelection()
  const [searchText, setSearchText] = useState('')
  const [currencies, setCurrencies] = useState<CurrencyItem[]>([])
  const [loading, setLoading] = useState(true)

  // 获取可充值币种数据
  const fetchRechargeableChains = async () => {
    try {
      setLoading(true)
      const data = await APIWeb.getRechargeableChains()
      // 使用 CoboSelectItem 的 chainList 属性
      const currencyItems = data.map((chain: any) => ({
        icon: chain.IconUrl, // 使用API返回的图标或默认图标
        name: chain.Symbol,
        description: `${chain.Name}`,
        symbol: chain.Symbol,
        chainList: chain.ChainList || []
      }))
      setCurrencies(currencyItems)
    } catch (error) {
      console.error('获取可充值币种失败:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRechargeableChains()
  }, [])

  // 处理币种选择
  const handleSelectCurrency = useCallback((currency: CurrencyItem) => {
    // 使用AppStateContext存储选中的币种信息
    actions.setSelectedCurrency(currency)
    // 清除之前的网络选择
    actions.setCurrentNetwork({ chainId: '', chainName: '', symbol: '' })
    // 跳转到选择网络页面（正常跳转，不使用replace）
    navigate('/recharge/select-network')
  }, [actions, navigate])

  // 过滤搜索结果
  const filteredCurrencies = useMemo(() => {
    if (!searchText) return currencies
    return currencies.filter(currency =>
      currency.name.toLowerCase().includes(searchText.toLowerCase()) ||
      currency.description.toLowerCase().includes(searchText.toLowerCase())
    )
  }, [currencies, searchText])

  const CurrencyItems = useMemo(
    () => filteredCurrencies.map((item, index) => (
      <Ripple key={index} className='rounded-2 w-full'>
        <div
          key={index}
          onClick={() => handleSelectCurrency(item)}
          className="w-full flex items-center py-4"
        >
          <div className="flex items-center">
            <div className="w-7 h-7 mr-3 bg-gray-100 rounded-full flex items-center justify-center">
              <img className="w-full h-full object-cover" src={item.icon} alt={item.name} />
            </div>
            <div className="text-left">
              <div className="font-medium text-base text-black">{item.name}</div>
              <div className="text-xs text-[#727272]">{item.description}</div>
            </div>
          </div>
        </div>
      </Ripple>
    )),
    [filteredCurrencies, handleSelectCurrency]
  )

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<div className="text-3.75 font-medium text-gray-900">{t('选择币种')}</div>}
        rightChildren={
          <div className='flex items-center'>
            <div className='w-6 h-6 mr-2'>
              <img src="/icon/question.svg" />
            </div>
            <div className='w-6 h-6' onClick={() => navigate('/recharge/deposit-records')}>
              <img src="/icon/record.svg" />
            </div>
          </div>
        }
      />

      <div className="flex flex-col bg-white h-full">

        {/* Search */}
        <div className="flex items-center h-10 bg-[#F3F3F3] mx-6 my-2 rounded-full flex-shrink-0">
          <div className='w-6 h-6 mr-2 ml-4 flex items-center'>
            <img src="/icon/search-icon.svg" />
          </div>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('搜索')}
            className="flex-1 bg-transparent outline-none text-3 text-black placeholder-[#1A1A1A]/60"
          />
        </div>


        {/* All Currencies */}
        <div className="px-6 pt-2 flex-1 overflow-y-auto">
          <h3 className="text-sm font-medium text-black">
            {searchText ? `` : t('热门')}
          </h3>
          <div className="bg-white">
            {loading ? (
              <div className="flex justify-center my-10">
                <Loading type="ball" />
              </div>
            ) : filteredCurrencies.length > 0 ? (
              CurrencyItems
            ) : (
              <div className="text-center py-10 text-gray-500">
                {searchText ? t('未找到相关币种') : t('暂无可用币种')}
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  )
})